import { useEffect, useRef, useState } from "react";
import { type OnboardingMode, useEmbeddedSignupConfig, useExchangeEmbeddedSignup } from "./api.js";

/**
 * In-panel WhatsApp Embedded Signup launcher — admin-only, shown in the Connection panel
 * when the number is not yet connected. The OAuth `code` is exchanged SERVER-SIDE; the access
 * token is never seen in the browser. The admin picks the mode per number:
 *   - "coexistence": keep the number on the WhatsApp Business app + add Cloud API. Declared by
 *     `extras.featureType` (the config_id selects the variation + version; no extras.version).
 *   - "migration": move the number FULLY to Cloud API. Standard onboarding — `featureType` is
 *     OMITTED. The merchant must FIRST delete the number from the WhatsApp Business app (this is
 *     destructive — chat history is lost and the app stops working on that number). The server
 *     then registers the number (see embedded-signup.ts). See docs/PHASE-0-IN-PANEL-EMBEDDED-SIGNUP-SPEC.md.
 */

interface FbLoginResponse {
  authResponse?: { code?: string };
}
interface FbSdk {
  init(p: { appId: string; autoLogAppEvents?: boolean; xfbml?: boolean; version: string }): void;
  login(cb: (r: FbLoginResponse) => void, opts: Record<string, unknown>): void;
}
declare global {
  interface Window {
    FB?: FbSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_ID = "facebook-jssdk";
const SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const MIST = "var(--mist, #888)";
const DANGER = "var(--danger, #c5221f)";
const WARN = "var(--warn, #b06000)";
const OK = "var(--ok, #137333)";

export function EmbeddedSignup({
  businessId,
  wabaSlug,
  numberSlug,
}: {
  businessId: string;
  wabaSlug: string | null;
  numberSlug: string | null;
}) {
  const cfgQ = useEmbeddedSignupConfig(wabaSlug);
  const exchange = useExchangeEmbeddedSignup(businessId);
  const [sdkReady, setSdkReady] = useState(false);
  const [mode, setMode] = useState<OnboardingMode>("coexistence");
  const [err, setErr] = useState<string | null>(null);
  const captured = useRef<{ waba_id: string | null; phone_number_id: string | null }>({
    waba_id: null,
    phone_number_id: null,
  });

  const cfg = cfgQ.data;
  const appId = cfg?.appId;
  const configId = cfg?.configId;
  const graphVersion = cfg?.graphVersion;
  const available = Boolean(cfg?.available && appId && configId);

  // Load the Facebook JS SDK once the (public) config is known, then FB.init.
  useEffect(() => {
    if (!available || !appId || !graphVersion) return;
    window.fbAsyncInit = () => {
      window.FB?.init({ appId, autoLogAppEvents: true, xfbml: false, version: graphVersion });
      setSdkReady(true);
    };
    if (document.getElementById(SDK_ID)) {
      if (window.FB) window.fbAsyncInit();
      return;
    }
    const js = document.createElement("script");
    js.id = SDK_ID;
    js.async = true;
    js.src = SDK_SRC;
    document.body.appendChild(js);
  }, [available, appId, graphVersion]);

  // Capture waba_id / phone_number_id from Meta's WA_EMBEDDED_SIGNUP postMessage — the
  // source of truth for the ids (the OAuth code alone does not carry them).
  useEffect(() => {
    function onMsg(e: MessageEvent) {
      let host: string;
      try {
        host = new URL(e.origin).hostname;
      } catch {
        return;
      }
      if (!/\.facebook\.com$/.test(host) && host !== "facebook.com") return;
      let data: { type?: string; data?: { waba_id?: string; phone_number_id?: string } };
      try {
        data = JSON.parse(typeof e.data === "string" ? e.data : "{}");
      } catch {
        return;
      }
      if (data?.type !== "WA_EMBEDDED_SIGNUP") return;
      if (data.data?.waba_id) captured.current.waba_id = data.data.waba_id;
      if (data.data?.phone_number_id) captured.current.phone_number_id = data.data.phone_number_id;
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  if (cfgQ.isLoading) return null;
  if (!available || !numberSlug) {
    return (
      <p style={{ marginTop: 12, color: MIST, fontSize: 13 }}>
        Embedded Signup isn’t enabled yet. It turns on after Tech Provider approval (the app must be
        Live) and <code>FEATURE_EMBEDDED_SIGNUP</code> is set on the server.
      </p>
    );
  }

  function launch() {
    setErr(null);
    if (!window.FB || !configId) {
      setErr("Facebook SDK not ready yet — wait a moment and retry.");
      return;
    }
    // ES v4 extras: coexistence is declared by featureType; migration OMITS it (standard
    // onboarding of the number the merchant just freed from the Business app). No extras.version.
    const extras: Record<string, unknown> = { setup: {}, sessionInfoVersion: "3" };
    if (mode === "coexistence") extras.featureType = "whatsapp_business_app_onboarding";
    window.FB.login(
      (resp) => {
        const code = resp?.authResponse?.code;
        if (!code) {
          setErr("No code returned (popup closed or login cancelled).");
          return;
        }
        exchange.mutate({
          mode,
          ...(cfg?.wabaSlug ? { wabaSlug: cfg.wabaSlug } : {}),
          numberSlug: numberSlug as string,
          code,
          wabaId: captured.current.waba_id,
          phoneNumberId: captured.current.phone_number_id,
        });
      },
      {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        extras,
      },
    );
  }

  const res = exchange.data;

  return (
    <div style={{ marginTop: 14 }}>
      {!res?.ok && (
        <>
          <fieldset style={{ border: 0, margin: 0, padding: 0, marginBottom: 10 }}>
            <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Onboarding mode
            </legend>
            <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
              <input
                type="radio"
                name="es-mode"
                value="coexistence"
                checked={mode === "coexistence"}
                disabled={exchange.isPending}
                onChange={() => setMode("coexistence")}
              />{" "}
              <strong>Coexistence</strong> — keep the number on the WhatsApp Business app and add
              the panel. History is imported. (Needs Tech Provider approval + Live app.)
            </label>
            <label style={{ display: "block", fontSize: 13 }}>
              <input
                type="radio"
                name="es-mode"
                value="migration"
                checked={mode === "migration"}
                disabled={exchange.isPending}
                onChange={() => setMode("migration")}
              />{" "}
              <strong>Migration</strong> — move the number fully to the panel. The WhatsApp Business
              app stops working on this number.
            </label>
          </fieldset>
          {mode === "migration" && (
            <p
              style={{
                color: WARN,
                fontSize: 13,
                marginBottom: 10,
                padding: "8px 10px",
                border: `1px solid ${WARN}`,
                borderRadius: 6,
              }}
            >
              ⚠ Before continuing, the merchant must{" "}
              <strong>delete this number from the WhatsApp Business app</strong> (Settings → Account
              → Delete my account), then wait ~5 minutes. This is destructive: chat history is lost
              and the app can no longer use this number.
            </p>
          )}
          <button
            type="button"
            className="btn btn--primary"
            disabled={!sdkReady || exchange.isPending}
            onClick={launch}
          >
            {exchange.isPending
              ? "Connecting…"
              : mode === "migration"
                ? "Migrate WhatsApp number"
                : "Connect WhatsApp number"}
          </button>
        </>
      )}
      {err && <p style={{ color: DANGER, fontSize: 13, marginTop: 8 }}>{err}</p>}
      {exchange.isError && (
        <p style={{ color: DANGER, fontSize: 13, marginTop: 8 }}>
          Exchange failed. Check the server logs and retry.
        </p>
      )}
      {res?.ok && (
        <div style={{ marginTop: 10, fontSize: 13 }}>
          <p style={{ color: OK, fontWeight: 600 }}>✓ Captured (non-secret — safe to record):</p>
          <p style={{ fontFamily: "ui-monospace, monospace" }}>
            WABA_ID = {res.wabaId ?? "?"}
            <br />
            phone_number_id = {res.phoneNumberId ?? "?"}
            {res.displayPhoneNumber ? (
              <>
                <br />
                display = {res.displayPhoneNumber}
              </>
            ) : null}
          </p>
          {res.mode === "migration" &&
            res.registration &&
            (res.registration.success ? (
              <p style={{ color: OK }}>✓ Registered on Cloud API.</p>
            ) : res.registration.attempted ? (
              <p style={{ color: DANGER }}>
                Registration failed
                {res.registration.errorCode ? ` (${res.registration.errorCode})` : ""}:{" "}
                {res.registration.message}
                {res.registration.retryable ? " You can retry." : ""}
              </p>
            ) : (
              <p style={{ color: WARN }}>Number not registered: {res.registration.skippedReason}</p>
            ))}
          <p style={{ color: MIST }}>{res.note}</p>
        </div>
      )}
    </div>
  );
}
